import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import PageShell, { Breadcrumb } from "@/components/feature/PageShell";

export default function Contact() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("company_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "company_alt" && typeof value === "string") {
        payload.append(key, value);
      }
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/d972e6dtfb6ioa7s1u90", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || t("contact.error.submit");
        setErrorMsg(serverMsg);
        setStatus("error");
      }
    } catch {
      setErrorMsg(t("contact.error.network"));
      setStatus("error");
    }
  };

  return (
    <PageShell>
      <section className="relative pt-24 pb-20 bg-gradient-to-b from-primary-800 to-primary-700 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20elegant%20geometric%20pattern%20with%20flowing%20communication%20lines%20and%20subtle%20envelope%20motifs%2C%20deep%20burgundy%20background%2C%20modern%20contact%20aesthetic%2C%20warm%20lighting%2C%20no%20text%2C%20no%20people&width=1600&height=600&seq=contact-hero-01&orientation=landscape')] bg-cover bg-center opacity-15"></div>
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8">
          <Breadcrumb items={[{ label: t("contact.title") }]} />
          <div className="mt-6 max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
              {t("contact.title")}
            </h1>
            <p className="mt-4 text-lg text-background-100/80 leading-relaxed">
              {t("contact.subtitle")}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="bg-background-50 rounded-lg p-8 border border-background-200">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">{t("contact.form.title")}</h2>
                {status === "success" ? (
                  <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-lg mb-1">{t("contact.success.title")}</h3>
                    <p className="text-sm text-foreground-600">{t("contact.success.desc")}</p>
                    <button onClick={() => setStatus("idle")} className="mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
                      {t("contact.success.new")}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                    {status === "error" && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">{errorMsg}</div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("contact.name")} <span className="text-red-500">*</span></label>
                        <input id="contact_name" name="name" type="text" required className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder={t("contact.placeholder.name")} />
                      </div>
                      <div>
                        <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("contact.email")} <span className="text-red-500">*</span></label>
                        <input id="contact_email" name="email" type="email" required className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder={t("contact.placeholder.email")} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact_org" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("contact.organization")}</label>
                      <input id="contact_org" name="organization" type="text" className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder={t("contact.placeholder.org")} />
                    </div>
                    <div>
                      <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("contact.subject")} <span className="text-red-500">*</span></label>
                      <input id="contact_subject" name="subject" type="text" required className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder={t("contact.placeholder.subject")} />
                    </div>
                    <div>
                      <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("contact.message")} <span className="text-red-500">*</span></label>
                      <textarea id="contact_message" name="message" required maxLength={500} rows={5} className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none" placeholder={t("contact.placeholder.message")}></textarea>
                      <p className="text-xs text-foreground-400 mt-1">{t("contact.chars")}</p>
                    </div>
                    <div className="hp-field-alt">
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button type="submit" className="bg-primary-600 text-background-50 px-8 py-3 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                      {t("contact.submit")}
                    </button>
                  </form>
                )}
              </div>
            </div>

            <aside className="space-y-6">
              <div className="bg-primary-50 rounded-lg p-6 border border-primary-200">
                <h3 className="font-heading font-semibold text-primary-800 mb-4">{t("contact.info")}</h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t("contact.address.label")}</p>
                      <p className="text-sm text-foreground-600">{t("contact.address")}</p>
                    </div>
                  </div>
                  {/* Email kept in record: info@iabs.hk */}
                  <div className="flex gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t("contact.phone.label")}</p>
                      <p className="text-sm text-foreground-600">+852 2856 0333</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-lg p-6 border border-accent-200">
                <h3 className="font-heading font-semibold text-accent-800 mb-3">{t("contact.hours")}</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-foreground-700"><strong className="text-foreground-900">{t("contact.hours.weekday")}：</strong>09:00 - 18:00</p>
                  <p className="text-foreground-700"><strong className="text-foreground-900">{t("contact.hours.saturday")}：</strong>09:00 - 13:00</p>
                  <p className="text-foreground-700"><strong className="text-foreground-900">{t("contact.hours.sunday")}：</strong>{t("contact.hours.closed")}</p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <div className="overflow-hidden rounded-lg border border-background-200">
            <iframe
              src="https://maps.google.com/maps?q=395+King's+Road,+North+Point,+Hong+Kong&output=embed"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="IABS Office Location"
            ></iframe>
          </div>
        </div>
      </section>
    </PageShell>
  );
}